import { useEffect, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { Copy, ListPlus, Search } from 'lucide-react';
import { toast } from 'sonner';
import { useCifs, useGerarCifsDoMes } from '@/api/cifs';
import { useRateio } from '@/api/rateios';
import { usePode } from '@/api/me';
import { BarraDeAcoesLista, usePaginarLista } from '@/components/barra-lista';
import { ErroLista } from '@/components/cargos';
import { ReabrirRateioDialog } from '@/components/rateios/reabrir-rateio-dialog';
import { Button } from '@/components/ui/button';
import { MonthPicker } from '@/components/ui/month-picker';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useDebounced } from '@/hooks/use-debounced';
import { BotaoRelatorios } from '@/relatorios';
import { formatarContagem, formatarData, formatarMoeda } from '@/lib/format';
import { useSessao } from '@/lib/session';
import { cn } from '@/lib/utils';
import { competenciaAtual, rotuloCompetencia } from '@/components/centros-custo/tipos';
import { CifDetalhe, type BuscaCifs } from './cif-detalhe';
import { CopiarCifsDialog } from './copiar-cifs-dialog';


/** `validateSearch`: descarta o que nao for valido em vez de quebrar a tela. */
export function lerBuscaCifs(bruto: Record<string, unknown>): BuscaCifs {
  const texto = typeof bruto.q === 'number' ? String(bruto.q) : bruto.q;
  const q = typeof texto === 'string' && texto.trim() ? texto.trim().slice(0, 100) : undefined;
  const comp = typeof bruto.comp === 'string' && /^\d{4}-(0[1-9]|1[0-2])$/.test(bruto.comp) ? bruto.comp : undefined;
  const id = typeof bruto.id === 'string' && /^[A-Za-z0-9_-]{1,64}$/.test(bruto.id) ? bruto.id : undefined;
  return {
    ...(q ? { q } : {}),
    ...(comp ? { comp } : {}),
    ...(id ? { id } : {}),
  };
}

/** Tela "Lancamentos de CIF" (/custos/cifs): lista por competencia com busca; registro abre por `?id=`. */
export function CifsPage({ busca }: { busca: BuscaCifs }) {
  const { empresaId } = useSessao();
  const navigate = useNavigate();
  const { pode } = usePode();
  const podeEditar = pode('rateio.editar');
  const verCusto = pode('custo.ver');
  const comp = busca.comp ?? competenciaAtual();

  /** `semBloqueio`: so a sincronizacao do `q` do debounce (troca o parametro da mesma tela, nada se perde); o resto segue o bloqueio. */
  const mudar = (mudanca: Partial<BuscaCifs>, opcoes?: { semBloqueio?: boolean }) => {
    void navigate({ to: '/custos/cifs', search: (anterior) => ({ ...anterior, ...mudanca }), replace: true, ...(opcoes?.semBloqueio ? { ignoreBlocker: true } : {}) });
  };
  const [copiarAberto, setCopiarAberto] = useState(false);
  const [reabrirAberto, setReabrirAberto] = useState(false);
  const gerar = useGerarCifsDoMes();
  /** Status do rateio da competencia (so se pode ver o rateio): fechado = CIFs travadas. */
  const rateio = useRateio(comp, pode('rateio.ver'));
  const rateioFechado = rateio.data?.status === 'FECHADO';
  /** Competencia em que a API disse que o rateio do destino esta fechado (previa ou 409 da copia). */
  const [fechadaPara, setFechadaPara] = useState<string | null>(null);
  const [termo, setTermo] = useState(busca.q ?? '');
  const termoAtrasado = useDebounced(termo.trim(), 300);
  useEffect(() => {
    if (termoAtrasado !== (busca.q ?? '')) mudar({ q: termoAtrasado || undefined }, { semBloqueio: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [termoAtrasado]);

  const lista = useCifs({
    competencia: comp,
    ...(termoAtrasado ? { q: termoAtrasado } : {}),
  });
  const itens = lista.data?.itens ?? [];
  const pagItens = usePaginarLista(itens);
  /** Mesmo sinal de bloqueio da tela: CIF rateada (bloqueada) = rateio fechado; ou a API ja disse que o destino esta fechado. */
  const fechada = fechadaPara === comp || itens.some((c) => c.bloqueada) || rateioFechado;

  let corpo;
  if (empresaId === null) corpo = <Mensagem>Escolha a empresa no menu lateral para ver os lançamentos de CIF.</Mensagem>;
  else if (lista.isError && !lista.data) corpo = <div className="p-4"><ErroLista erro={lista.error} onTentar={() => void lista.refetch()} titulo="Não foi possível carregar os lançamentos de CIF" /></div>;
  else if (!lista.data) corpo = <Mensagem papel="status">Carregando lançamentos de CIF…</Mensagem>;
  else if (itens.length === 0) {
    const filtrado = Boolean(busca.q);
    corpo = filtrado ? (
      <Mensagem papel="status">
        Nenhum lançamento de CIF com esses filtros.{' '}
        <button type="button" className="font-medium text-primary hover:underline" onClick={() => void navigate({ to: '/custos/cifs', search: { comp: busca.comp }, replace: true })}>
          Limpar filtros
        </button>
      </Mensagem>
    ) : (
      <Mensagem papel="status">
        Nenhum lançamento de CIF nesta competência.
        {podeEditar && (
          <>
            {' '}
            <Link to="/custos/cifs" search={{ ...busca, comp, id: 'novo' }} className="font-medium text-primary hover:underline">
              Cadastre o primeiro
            </Link>
            . Use &quot;Copiar de outro mês&quot; para trazer os lançamentos do mês anterior.
          </>
        )}
      </Mensagem>
    );
  } else {
    corpo = (
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] border-collapse text-small" aria-label="Lista de lançamentos de CIF">
          <thead>
            <tr className="text-left text-caption text-ink-muted">
              <th scope="col" className="px-4 py-2.5 font-medium">Identificador</th>
              <th scope="col" className="px-3 py-2.5 font-medium">Descrição</th>
              <th scope="col" className="px-3 py-2.5 font-medium">Item</th>
              <th scope="col" className="px-3 py-2.5 font-medium">Competência</th>
              <th scope="col" className="px-3 py-2.5 font-medium">Vencimento</th>
              {verCusto && <th scope="col" className="px-3 py-2.5 font-medium">Valor</th>}
              <th scope="col" className="px-4 py-2.5 font-medium">Rateada?</th>
            </tr>
          </thead>
          <tbody>
            {pagItens.visiveis.map((c) => (
              <tr
                key={c.id}
                data-testid="linha-cif"
                onClick={() => void navigate({ to: '/custos/cifs', search: (a) => ({ ...a, id: c.id }) })}
                className={cn('cursor-pointer border-t border-line-soft hover:bg-surface-muted', busca.id === c.id && 'bg-line-soft')}
              >
                <td className="px-4 py-3 font-semibold">{c.identificador}</td>
                <td className="px-3 py-3 text-ink-secondary">
                  {c.descricao}
                  {c.copiadaDe && <span className="ml-2 whitespace-nowrap rounded-full bg-line-soft px-2 py-0.5 text-caption text-ink-muted">copiada de {c.copiadaDe}</span>}
                </td>
                <td className="px-3 py-3 text-ink-secondary">{c.itemCifNome ?? '—'}</td>
                <td className="tabular px-3 py-3 text-ink-secondary">{rotuloCompetencia(c.competencia)}</td>
                <td className="tabular px-3 py-3 text-ink-secondary">{formatarData(c.vencimento)}</td>
                {verCusto && <td className="tabular px-3 py-3 text-ink-secondary">{formatarMoeda(c.valor)}</td>}
                <td className="px-4 py-3">
                  {c.bloqueada ? (
                    <span className="rounded-full bg-success-bg px-2 py-0.5 text-caption font-semibold text-success-ink">Rateada</span>
                  ) : (
                    <span className="text-ink-muted">Não</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-[1100px] flex-col gap-5 pb-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-small text-ink-muted">
            Custos <span aria-hidden="true">/</span> Lançamentos de CIF
          </div>
          <h1 className="mt-1 text-display font-semibold tracking-[-0.01em]">Lançamentos de CIF</h1>
        </div>
        {podeEditar && (
          <div className="flex flex-wrap items-center gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex">
                  <Button variant="outline" disabled={fechada || !pode('custo.ver')} title={pode('custo.ver') ? undefined : 'Copiar do mês mostra e edita valores: exige permissão de custo.'} onClick={() => setCopiarAberto(true)} className="h-[38px] gap-1.5 rounded-[10px] px-4">
                    <Copy aria-hidden="true" className="size-[15px]" />
                    Copiar de outro mês
                  </Button>
                </span>
              </TooltipTrigger>
              {fechada && <TooltipContent>Rateio desta competência já está fechado: as CIFs não podem ser copiadas para ela.</TooltipContent>}
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex">
                  <Button
                    variant="outline"
                    disabled={fechada || gerar.isPending}
                    onClick={() =>
                      gerar.mutate(
                        { competencia: comp },
                        {
                          onSuccess: (r) => toast.success(`${r.geradas} geradas, ${r.puladas} já existiam, ${r.semApropriacao} sem apropriação`),
                          onError: (e) => toast.error(`Não foi possível gerar os lançamentos: ${e.message}`),
                        },
                      )
                    }
                    className="h-[38px] gap-1.5 rounded-[10px] px-4"
                  >
                    <ListPlus aria-hidden="true" className="size-[15px]" />
                    Gerar lançamentos do mês a partir dos itens
                  </Button>
                </span>
              </TooltipTrigger>
              {fechada && <TooltipContent>Rateio desta competência já está fechado: não é possível gerar lançamentos.</TooltipContent>}
            </Tooltip>
          </div>
        )}
      </div>

      <section aria-label="Lista de lançamentos de CIF" aria-busy={lista.isFetching} className="rounded-2xl border border-border bg-card">
        {rateioFechado && (
          <div role="status" className="flex flex-wrap items-center justify-between gap-3 rounded-t-2xl border-b border-warning-ink/30 bg-warning-bg px-4 py-3 text-small text-warning-ink">
            <span>Rateio de {rotuloCompetencia(comp)} fechado: CIFs travadas</span>
            {podeEditar && (
              <Button type="button" variant="secondary" onClick={() => setReabrirAberto(true)}>
                Reabrir rateio
              </Button>
            )}
          </div>
        )}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3.5">
          <div className="flex h-[34px] w-[260px] max-w-full items-center gap-2 rounded-[9px] border border-input bg-card px-2.5 focus-within:border-primary focus-within:shadow-focus">
            <Search aria-hidden="true" className="size-[15px] text-ink-muted" />
            <label htmlFor="cif-q" className="sr-only">
              Buscar lançamento de CIF
            </label>
            <input id="cif-q" type="search" value={termo} onChange={(e) => setTermo(e.target.value)} placeholder="Identificador, descrição ou documento" autoComplete="off" className="w-full min-w-0 bg-transparent text-small outline-none" />
          </div>
          <div className="flex items-center gap-2">
            <label htmlFor="cif-comp" className="text-small text-ink-secondary">Competência</label>
            <MonthPicker id="cif-comp" value={comp} onChange={(c) => mudar({ comp: c })} />
          </div>
        </div>
        {corpo}
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-4 py-3 text-small text-ink-secondary" data-testid="contagem-cifs">
          <span>{lista.data ? `${formatarContagem(itens.length)} lançamento${itens.length === 1 ? '' : 's'} na lista` : ' '}</span>
          {lista.data && verCusto && lista.data.somaValor !== undefined && (
            <span className="tabular font-semibold text-ink-body">Total: {formatarMoeda(lista.data.somaValor)}</span>
          )}
        </div>
      </section>

      {podeEditar && (
        <ReabrirRateioDialog
          competencia={comp}
          aberto={reabrirAberto}
          onFechar={() => setReabrirAberto(false)}
          onReaberto={() => setFechadaPara(null)}
        />
      )}

      {copiarAberto && (
        <CopiarCifsDialog destino={comp} onFechar={() => setCopiarAberto(false)} onDestinoFechado={() => setFechadaPara(comp)} />
      )}

      {busca.id === 'novo' && !podeEditar ? (
        <section className="rounded-2xl border border-border bg-card p-5 text-small text-ink-body" role="status">
          Seu acesso não permite cadastrar lançamentos de CIF.
        </section>
      ) : (
        busca.id && <CifDetalhe key={busca.id} id={busca.id === 'novo' ? undefined : busca.id} busca={busca} podeEditar={podeEditar} />
      )}

      <BarraDeAcoesLista
        novo={podeEditar ? { rotulo: 'Novo lançamento', onClick: () => void navigate({ to: '/custos/cifs', search: { ...busca, comp, id: 'novo' } }) } : undefined}
        paginacao={lista.data ? pagItens.paginacao : undefined}
        relatorios={<BotaoRelatorios tela="cifs" params={{ comp, q: busca.q }} />}
      />
    </div>
  );
}

function Mensagem({ children, papel }: { children: React.ReactNode; papel?: 'status' }) {
  return (
    <p role={papel} className="px-5 py-10 text-center text-small text-ink-muted">
      {children}
    </p>
  );
}
